import React, { useEffect, useRef, useState } from "react";
import { useHistory, useLocation } from "react-router-dom";

import SearchIcon from "@mui/icons-material/Search";
import AddIcon from "@mui/icons-material/Add";
import ClearIcon from "@mui/icons-material/Clear";
import TuneIcon from "@mui/icons-material/Tune";
import PersonIcon from "@mui/icons-material/Person";
import CreditScoreIcon from "@mui/icons-material/CreditScore";
import DescriptionIcon from "@mui/icons-material/Description";
import AccountBalanceIcon from "@mui/icons-material/AccountBalance";
import AccountBalanceWalletIcon from "@mui/icons-material/AccountBalanceWallet";
import PrecisionManufacturingIcon from "@mui/icons-material/PrecisionManufacturing";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import BadgeIcon from "@mui/icons-material/Badge";
import AccountCircleOutlinedIcon from "@mui/icons-material/AccountCircleOutlined";
import AssignmentIndIcon from '@mui/icons-material/AssignmentInd';
import EventNoteIcon from '@mui/icons-material/EventNote';
import AssessmentIcon from '@mui/icons-material/Assessment';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import RequestQuoteIcon from '@mui/icons-material/RequestQuote';
import PlaylistAddIcon from '@mui/icons-material/PlaylistAdd';

import request from "../../Helpers/API";
import Swal from "sweetalert2";
import { ClientesearchReducer } from "../../Reducers/ClientesearReducer";
import { useSelector } from "react-redux";
import { LoaderScreen } from "../components/LoaderScreen";
import { CRM_ModalBusquedaAvanzada } from "./CRM_ModalBusquedaAvanzada";

// Estado vacío para cuando no hay cliente seleccionado o el API no trae una sección
const DATOS_VACIO = {
  generales: { pendientes: 0, progreso: 0, ultima_actualizacion: null, fecha_creacion: null },
  solicitudes: { creadas: 0, enProceso: 0, finalizadas: 0, ultimaFechaFacturacion: null },
  cotizaciones: { objetivo: 0, creadas: 0, enProceso: 0, cerradas: 0, ultimaFechaFacturacion: null, facturacionPorLinea: [] },
  lineasCredito: [],
  referencias: { total: 0, bancos: [] },
  maquinaria: { total: 0, valor: 0, tipos: [] },
  documentacionMhusa: { documentos: 0, vigentes: 0, expirados: 0 },
  documentacionJDF: { documentos: 0, vigentes: 0, expirados: 0 },
  // El API no manda objetivo todavía — se deja fijo en 0 hasta que lo incluya
  visitas: { realizadas: 0, programadas: 0, objetivo: 0 },
  parqueMaquinaria: { total: 0, john_deere: 0, otras_marcas: 0, porcentaje_john_deere: 0 },
};

// Completa con DATOS_VACIO cualquier sección que el API todavía no regrese, para no romper el render
const normalizarDashboard = (data) => {
  const api = data || {};

  return {
    generales: { ...DATOS_VACIO.generales, ...(api.generales || {}) },
    solicitudes: {
      ...DATOS_VACIO.solicitudes,
      ...(api.solicitudes || {}),
      // El API puede regresar la propiedad como "UltimaFechaFacturacion" (PascalCase) o camelCase según el endpoint
      ultimaFechaFacturacion: api.solicitudes?.ultimaFechaFacturacion ?? api.solicitudes?.UltimaFechaFacturacion ?? null,
    },
    cotizaciones: {
      ...DATOS_VACIO.cotizaciones,
      ...(api.cotizaciones || {}),
      ultimaFechaFacturacion: api.cotizaciones?.ultimaFechaFacturacion ?? api.cotizaciones?.UltimaFechaFacturacion ?? null,
      // Última fecha de facturación por línea (JDF, Mhusa, etc.).
      // El API manda un array cuando hay varias líneas, pero un objeto suelto cuando solo hay una: se normaliza a array en ambos casos.
      facturacionPorLinea: Array.isArray(api?.facturacion_cotizaciones)
        ? api.facturacion_cotizaciones
        : api?.facturacion_cotizaciones && typeof api.facturacion_cotizaciones === "object"
          ? [api.facturacion_cotizaciones]
          : [],
    },
    lineasCredito: Array.isArray(api.lineasCredito) ? api.lineasCredito : [],
    referencias: {
      ...DATOS_VACIO.referencias,
      ...(api.referencias || {}),
      bancos: Array.isArray(api.referencias?.bancos) ? api.referencias.bancos : [],
    },
    maquinaria: {
      ...DATOS_VACIO.maquinaria,
      ...(api.maquinaria || {}),
      tipos: Array.isArray(api.maquinaria?.tipos) ? api.maquinaria.tipos : [],
    },
    documentacionMhusa: { ...DATOS_VACIO.documentacionMhusa, ...(api.documentacionMhusa || {}) },
    documentacionJDF: { ...DATOS_VACIO.documentacionJDF, ...(api.documentacionJDF || {}) },
    // objetivo aún no viene del API — se mantiene en 0 aunque lleguen realizadas/programadas
    visitas: { ...DATOS_VACIO.visitas, ...(api.visitas || {}) },
    // mdl_Dashboard_CRM_Parque_Maquinaria — el API manda la propiedad como "parque_maquinaria" (snake_case)
    parqueMaquinaria: { ...DATOS_VACIO.parqueMaquinaria, ...(api.parque_maquinaria || {}) },
  };
};

const STATUS_CFG = {
  ok: { icon: <CheckCircleOutlineIcon />, label: "Saludable", cls: "status-ok" },
  warning: { icon: <WarningAmberIcon />, label: "Atención", cls: "status-warning" },
  critical: { icon: <ErrorOutlineIcon />, label: "Crítico", cls: "status-critical" },
};

const formatMXN = (n) => `$${new Intl.NumberFormat("es-MX").format(n)}`;

// Agrupa las líneas de crédito por descripción y suma su saldo_total en cada grupo
const agruparLineasPorDescripcion = (lineasCredito) => {
  const grupos = {};
  lineasCredito.forEach((l) => {
    const descripcion = l.descripcion || "Sin descripción";
    grupos[descripcion] = (grupos[descripcion] || 0) + Number(l.saldo_total || 0);
  });
  return Object.entries(grupos).map(([descripcion, total]) => ({ descripcion, total }));
};

// Secciones cuyo endpoint aún no está listo — se muestran deshabilitadas.
// Para reactivar una sección: quitar su llave de aquí en cuanto GetDashboardCRM la incluya.
const SECCIONES_PENDIENTES = {
  cliente: false,
  lineasCredito: false,
  referencias: false,
  maquinaria: false,
  cotizaciones: false

};

// Datos del cliente seleccionado — en producción vienen del API
const DATOS_CLIENTE_DEMO = {
  expediente: {
    status: "warning",
    pendientes: 4,
    tareas: 2,
    progress: 72,
    lastUpdate: "Hoy, 09:15",
  },
  solicitudes: { creadas: 20, enProceso: 50, finalizadas: 30 },
  cotizaciones: { creadas: 5, enProceso: 12, cerradas: 8 },
  lineasCredito: [
    { tipo: "Maquinaria", monto: 4500000 },
    { tipo: "Refacciones", monto: 750000 },
    { tipo: "Servicio", monto: 350000 },
    { tipo: "John Deere Financial", monto: 8000000 },
    { tipo: "Enganches", monto: 500000 },
  ],
  referencias: { total: 3, bancos: ["BBVA", "Banorte", "Santander"] },
  maquinaria: {
    total: 42,
    valor: 45000000,
    tipos: [
      { nombre: "Tractores", cantidad: 15 },
      { nombre: "Cosechadoras", cantidad: 8 },
      { nombre: "Implementos", cantidad: 12 },
      { nombre: "Pulverizadoras", cantidad: 7 },
    ],
  },
};

export const CRM_Dashboard = () => {
  const history = useHistory();
  const location = useLocation();

  const [busqueda, setBusqueda] = useState("");
  const [mostrarDropdown, setMostrarDropdown] = useState(false);
  const [clienteSeleccionado, setClienteSeleccionado] = useState(null);
  const [clientes, setClientes] = useState([]);
  const [dashboard, setDashboard] = useState(DATOS_VACIO);
  // Carga única con idcliente=0 al entrar — de todo lo que trae GetDashboardCRM en ese caso,
  // solo se usa la sección "visitas" (es la única que responde datos reales sin un cliente
  // buscado); el resto de las cards ignora esto y sigue mostrando su estado vacío normal
  const [dashboardGeneral, setDashboardGeneral] = useState(DATOS_VACIO);
  // Tab de la card Expediente Digital: alterna entre documentacionMhusa y documentacionJDF
  const [tipoExpedienteDigital, setTipoExpedienteDigital] = useState("Mhusa");
  const [loader, setLoader] = useState({ mostrar: false, alerta: false, error: false, mensaje: "" });
  const [modalBusquedaAvanzada, setModalBusquedaAvanzada] = useState(false);


  const { roles } = useSelector((state) => state.sesion);

  const rolesPermitidos = ["REWR", "MKAD"];


  const searchRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (searchRef.current && !searchRef.current.contains(e.target)) {
        setMostrarDropdown(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Listado de clientes del CRM — se carga una vez al entrar a la pantalla
  useEffect(() => {
    setLoader((prev) => ({ ...prev, mostrar: true }));
    request
      .get("Clientes/GetListadoClientesCRM")
      .then((result) => {
        setClientes(result || []);
        setLoader((prev) => ({ ...prev, mostrar: false }));
      })
      .catch((error) => {
        setLoader((prev) => ({ ...prev, mostrar: false }));
        Swal.fire("CLIENTES", JSON.stringify(error), "error");
      });
  }, []);

  // Carga inicial con idcliente=0 — sin esto la card de Visitas no tiene datos reales hasta
  // que se busca un cliente; el resto de las secciones de esta respuesta se ignora
  useEffect(() => {
    request
      .get("Clientes/GetDashboardCRM?idcliente=0")
      .then((result) => setDashboardGeneral(normalizarDashboard(result)))
      .catch(() => setDashboardGeneral(DATOS_VACIO));
  }, []);

  const resultados = clientes.filter((c) => {
    const q = busqueda.trim().toLowerCase();
    if (!q) return true;
    return (
      (c.razon_social || "").toLowerCase().includes(q) ||
      (c.rfc || "").toLowerCase().includes(q)
    );
  });

  const handleBusquedaChange = (e) => {
    setBusqueda(e.target.value);
    setMostrarDropdown(true);
    if (clienteSeleccionado) setClienteSeleccionado(null);
  };

  useEffect(() => {
    if (clienteSeleccionado !== null && clienteSeleccionado !== "" && clienteSeleccionado !== undefined) {
      setLoader((prev) => ({ ...prev, mostrar: true }));
      request
        .get(`Clientes/GetDashboardCRM?idcliente=${clienteSeleccionado.idcliente}`)
        .then((result) => {
          setDashboard(normalizarDashboard(result));
          setLoader((prev) => ({ ...prev, mostrar: false }));
        })
        .catch((error) => {
          setDashboard(DATOS_VACIO);
          setLoader((prev) => ({ ...prev, mostrar: false }));
          Swal.fire("CLIENTES", JSON.stringify(error), "error");
        });
    } else {
      setDashboard(DATOS_VACIO);
    }

  }, [clienteSeleccionado]);


  const handleSelectCliente = (cliente) => {
    setClienteSeleccionado(cliente);
    setBusqueda(cliente.razon_social);
    setMostrarDropdown(false);
  };

  // Si se regresa desde otra pantalla (ej. Solicitudes) con un idcliente en el state de
  // navegación, se vuelve a seleccionar ese cliente en cuanto el listado esté cargado
  useEffect(() => {
    const idclienteEntrante = location.state?.idcliente;
    if (!idclienteEntrante || clientes.length === 0) return;
    const cliente = clientes.find(
      (c) => Number(c.idcliente) === Number(idclienteEntrante)
    );
    if (cliente) handleSelectCliente(cliente);
  }, [location.state, clientes]);

  const handleLimpiar = () => {
    setBusqueda("");
    setClienteSeleccionado(null);
    setMostrarDropdown(false);
  };

  const navegarA = (ruta) => history.push(ruta);

  // Datos activos según selección
  const datos = clienteSeleccionado ? dashboard : DATOS_VACIO;
  const vacio = !clienteSeleccionado;

  // Con un cliente seleccionado navegan a su ruta llevando el idcliente; sin selección, las cards quedan sin función
  const navegarSiHayCliente = (ruta, extra = {}) => {
    if (vacio) return;
    history.push(ruta, { idcliente: clienteSeleccionado.idcliente, cliente: clienteSeleccionado.razon_social, pantalla: "Dashboard CRM", ...extra });
  };

  // Props de interacción compartidas por cada card — sin cliente seleccionado o sección pendiente, sin click/teclado/foco
  const cardInteractionProps = (ruta, pendiente = false, extra = {}) =>
    vacio || pendiente
      ? {}
      : {
        onClick: () => navegarSiHayCliente(ruta, extra),
        role: "button",
        tabIndex: 0,
        onKeyDown: (e) => e.key === "Enter" && navegarSiHayCliente(ruta, extra),
      };

  const onDercargarReferenciaBancaria = (idcliente) => {
    if (idcliente !== null && idcliente !== "" && idcliente !== undefined) {
      setLoader((prev) => ({ ...prev, mostrar: true }));
      request
        .get(`ReferenciaBancaria/ReportePDF?idcliente=${idcliente}`)
        .then((result) => {
          const linkSource = `data:application/pdf;base64,${result.documento}`;
          const downloadLink = document.createElement("a");
          const fileName = `${result.nombredocumento}.${result.extension}`;
          downloadLink.href = linkSource;
          downloadLink.download = fileName;
          downloadLink.click();
          setLoader((prev) => ({ ...prev, mostrar: false }));
        })
        .catch((error) => {
          setLoader((prev) => ({ ...prev, mostrar: false }));
          Swal.fire("INTERESES", JSON.stringify(error), "error");
        });
    }

  }

  // ── RENDERS ─────────────────────────────────────────────────────

  const renderStatusBadge = (status) => {
    if (!status) return null;
    const cfg = STATUS_CFG[status];
    return (
      <span className={`crm-status-badge ${cfg.cls}`}>
        {cfg.icon}{cfg.label}
      </span>
    );
  };

  const renderSinInfo = (pendiente = false) => (
    <p className="crm-sin-info">
      {pendiente
        ? "Sección en preparación. Estará disponible próximamente."
        : "Seleccione un cliente para visualizar esta información."}
    </p>
  );

  const renderCardExpediente = () => {
    const { pendientes, progreso, ultima_actualizacion, fecha_creacion } = datos.generales;
    const pendiente = SECCIONES_PENDIENTES.cliente;
    return (
      <div
        className={`crm-card crm-card-expediente${(vacio || pendiente) ? " crm-card-disabled" : ""}`}
        {...cardInteractionProps("/DashClientes/Detalle")}
      >
        <div className="crm-card-top">
          <div className="crm-card-top-left">
            <div className="crm-card-icon crm-icon-persona"><PersonIcon /></div>
            <h3 className="crm-card-title">Ficha de Cliente</h3>
          </div>
          {/* {renderStatusBadge(status)} */}
        </div>
        <div className="crm-card-body">
          {!vacio && (
            <p className="crm-card-desc">Datos generales, contactos, domicilios y expediente digital.</p>
          )}
        </div>
        {(vacio || pendiente) ? renderSinInfo(pendiente) : (
          <>
            <div className="crm-exp-stats">
              {pendientes > 0 && (
                <div className="crm-exp-stat crm-exp-stat-pending">
                  <span className="crm-stat-num">{pendientes}</span>
                  <span className="crm-stat-lbl">Pendientes</span>
                </div>
              )}
              {/* {tareas > 0 && (
                <div className="crm-exp-stat crm-exp-stat-tasks">
                  <span className="crm-stat-num">{tareas}</span>
                  <span className="crm-stat-lbl">Tareas</span>
                </div>
              )} */}
            </div>
            <div className="crm-exp-progress">
              <div className="crm-exp-progress-header">
                <span>Expediente completo</span>
                <span className="crm-exp-progress-pct">{progreso}%</span>
              </div>
              <div className="crm-exp-progress-track">
                <div className="crm-exp-progress-fill" style={{ width: `${progreso}%` }} />
              </div>
            </div>
          </>
        )}
        <div className="crm-card-footer">
          <span className="crm-card-last">
            {(vacio || pendiente) ? "—" : (
              <>
                {fecha_creacion && <>Creado: {fecha_creacion}</>}
                {fecha_creacion && ultima_actualizacion && <span className="crm-card-last-sep" />}
                {ultima_actualizacion && <>Actualizado: {ultima_actualizacion}</>}
              </>
            )}
          </span>
          <ArrowForwardIosIcon className="crm-card-arrow" />
        </div>
      </div>
    );
  };

  // clases por defecto = uso original (Solicitudes/Cotizaciones); Expediente Digital pasa las suyas (gris/verde/rojo)
  const renderBarraSegmentada = (a, b, c, clases = ["seg-gris", "seg-amarillo", "seg-verde"]) => {
    const total = a + b + c;
    if (total === 0) return <div className="crm-barra-track crm-barra-vacia" />;
    const [claseA, claseB, claseC] = clases;
    return (
      <div className="crm-barra-track">
        {a > 0 && <div className={claseA} style={{ flex: a }} />}
        {b > 0 && <div className={claseB} style={{ flex: b }} />}
        {c > 0 && <div className={claseC} style={{ flex: c }} />}
      </div>
    );
  };

  const renderCardSolicitudes = () => {
    const { creadas, enProceso, finalizadas } = datos.solicitudes;
    const total = creadas + enProceso + finalizadas;
    return (
      <div
        className={`crm-card crm-card-pipeline${vacio ? " crm-card-disabled" : ""}`}
        {...cardInteractionProps("/DashClientes/Solicitud_Credito")}
      >
        <div className="crm-card-top">
          <div className="crm-card-top-left">
            <div className="crm-card-icon crm-icon-credito"><CreditScoreIcon /></div>
            <h3 className="crm-card-title">Solicitudes de Crédito</h3>
          </div>
          <span className="crm-pipeline-total">{vacio ? "—" : `${total} total`}</span>
        </div>
        <div className="crm-card-body">
          {!vacio && datos.solicitudes.ultimaFechaFacturacion && (
            <p className="crm-card-desc">Última facturación: {datos.solicitudes.ultimaFechaFacturacion}</p>
          )}
        </div>
        {vacio ? renderSinInfo() : (
          <>
            <div className="crm-pipeline-counters">
              <div className="crm-pipeline-counter counter-gris">
                <span className="counter-num">{creadas}</span>
                <span className="counter-lbl">Creadas</span>
              </div>
              <div className="crm-pipeline-counter counter-amarillo">
                <span className="counter-num">{enProceso}</span>
                <span className="counter-lbl">En proceso</span>
              </div>
              <div className="crm-pipeline-counter counter-verde">
                <span className="counter-num">{finalizadas}</span>
                <span className="counter-lbl">Finalizadas</span>
              </div>
            </div>
            {renderBarraSegmentada(creadas, enProceso, finalizadas)}
            <div className="crm-barra-leyenda">
              <span className="ley-gris">■ Creadas</span>
              <span className="ley-amarillo">■ En proceso</span>
              <span className="ley-verde">■ Finalizadas</span>
            </div>
          </>
        )}
        <div className="crm-card-footer">
          <span className="crm-card-last">{vacio ? "—" : "Ver solicitudes"}</span>
          <ArrowForwardIosIcon className="crm-card-arrow" />
        </div>
      </div>
    );
  };

  // Igual que renderCardVisitas: sin cliente seleccionado se usa dashboardGeneral (idcliente=0,
  // trae los totales de todos los clientes) en vez de ocultar la card. El click navega a
  // Ventas/Cotizaciones llevando el idcliente cuando hay uno seleccionado (para que esa pantalla
  // filtre igual que el resto de cards); sin cliente, navega sin idcliente y muestra todo.
  const renderCardCotizaciones = () => {
    const { objetivo, creadas, enProceso, cerradas, ultimaFechaFacturacion, facturacionPorLinea } = vacio ? dashboardGeneral.cotizaciones : datos.cotizaciones;
    const pendiente = SECCIONES_PENDIENTES.cotizaciones;
    const total = creadas + enProceso + cerradas;
    const avanceObjetivo = objetivo > 0 ? Math.min(100, Math.round((total / objetivo) * 100)) : (total > 0 ? 100 : 0);
    const irACotizaciones = () => history.push(
      "/Ventas/Cotizaciones",
      clienteSeleccionado
        ? { idcliente: clienteSeleccionado.idcliente, cliente: clienteSeleccionado.razon_social, pantalla: "Dashboard CRM" }
        : { pantalla: "Dashboard CRM" }
    );

    return (
      <div
        className={`crm-card crm-card-pipeline${pendiente ? " crm-card-disabled" : ""}`}
        {...(pendiente ? {} : {
          onClick: irACotizaciones,
          role: "button",
          tabIndex: 0,
          onKeyDown: (e) => e.key === "Enter" && irACotizaciones(),
        })}
      >
        <div className="crm-card-top">
          <div className="crm-card-top-left">
            <div className="crm-card-icon crm-icon-cotizacion"><DescriptionIcon /></div>
            <h3 className="crm-card-title">Cotizaciones</h3>
          </div>
          <span className="crm-pipeline-total">{pendiente ? "—" : `${total} total`}</span>
        </div>
        {/* <div className="crm-card-body">
          {!(vacio || pendiente) && ultimaFechaFacturacion && (
            <p className="crm-card-desc">Última facturación: {ultimaFechaFacturacion}</p>
          )}
        </div> */}
        {pendiente ? renderSinInfo(pendiente) : (
          <>
            {facturacionPorLinea.length > 0 && (
              <div className="crm-cotizaciones-facturacion">
                <span className="crm-cotizaciones-facturacion-titulo">Facturación por línea</span>
                <div className="crm-lineas-tabla crm-cotizaciones-facturacion-list scroll">
                  {facturacionPorLinea.map((f) => (
                    <div className="crm-linea-row" key={f.linea}>
                      <span className="crm-linea-tipo">{f.linea}</span>
                      <span className="crm-linea-fecha">{f.ultima_fecha_facturacion}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
            <div className="crm-exp-progress">
              <div className="crm-exp-progress-header">
                <span>Avance</span>
                <span className="crm-exp-progress-pct">{avanceObjetivo}%</span>
              </div>
              <div className="crm-exp-progress-track">
                <div className="crm-exp-progress-fill" style={{ width: `${avanceObjetivo}%` }} />
              </div>
            </div>
            <div className="crm-barra-leyenda">
              <span className="ley-objetivo">Objetivo ({objetivo})</span>
              <span className="ley-gris">■ Creadas ({creadas})</span>
              <span className="ley-amarillo">■ En proceso ({enProceso})</span>
              <span className="ley-verde">■ Cerradas ({cerradas})</span>
            </div>
          </>
        )}
        <div className="crm-card-footer">
          <span className="crm-card-last">{pendiente ? "—" : "Ver cotizaciones"}</span>
          <ArrowForwardIosIcon className="crm-card-arrow" />
        </div>
      </div>
    );
  };

  // Objetivo/Programadas/Realizadas/Avance — mismas 4 métricas que CRM_Visitas_Screen.
  // GetDashboardCRM ya trae datos.visitas.{realizadas,programadas} reales tanto con idcliente=0
  // (dashboardGeneral, sin búsqueda) como con un cliente buscado (dashboard) — objetivo se deja
  // en 0 en ambos casos porque ese dato todavía no llega ahí.
  const renderCardVisitas = () => {
    const { objetivo, programadas, realizadas } = vacio ? dashboardGeneral.visitas : datos.visitas;
    const avance = objetivo > 0 ? Math.min(100, Math.round((realizadas / objetivo) * 100)) : (realizadas > 0 ? 100 : 0);

    return (
      <div
        className="crm-card crm-card-pipeline crm-card-visitas"
        onClick={() => history.push("/DashClientes/Visitas", clienteSeleccionado ? { idcliente: clienteSeleccionado.idcliente, cliente: clienteSeleccionado.razon_social, pantalla: "Dashboard CRM" } : {})}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => e.key === "Enter" && history.push("/DashClientes/Visitas")}
      >
        <div className="crm-card-top">
          <div className="crm-card-top-left">
            <div className="crm-card-icon crm-icon-visitas"><EventNoteIcon /></div>
            <h3 className="crm-card-title">{vacio ? "Historial de Visitas" : "Visitas del cliente"}</h3>
          </div>
        </div>

        <div className="crm-pipeline-counters crm-visitas-counters">
          <div className="crm-pipeline-counter counter-amarillo">
            <span className="counter-num">{objetivo}</span>
            <span className="counter-lbl">Objetivo</span>
          </div>
          <div className="crm-pipeline-counter counter-gris">
            <span className="counter-num">{programadas}</span>
            <span className="counter-lbl">Programadas</span>
          </div>
          <div className="crm-pipeline-counter counter-verde">
            <span className="counter-num">{realizadas}</span>
            <span className="counter-lbl">Realizadas</span>
          </div>
        </div>

        <div className="crm-exp-progress">
          <div className="crm-exp-progress-header">
            <span>Avance</span>
            <span className="crm-exp-progress-pct">{avance}%</span>
          </div>
          <div className="crm-exp-progress-track">
            <div className="crm-exp-progress-fill" style={{ width: `${avance}%` }} />
          </div>
        </div>

        <div className="crm-card-footer">
          <span className="crm-card-last">Ver visitas</span>
          <ArrowForwardIosIcon className="crm-card-arrow" />
        </div>
      </div>
    );
  };

  const renderCardLineas = () => {
    const { lineasCredito } = datos;
    const lineasAgrupadas = agruparLineasPorDescripcion(lineasCredito);
    const totalDisponible = lineasAgrupadas.reduce((s, l) => s + l.total, 0);

    const pendiente = SECCIONES_PENDIENTES.lineasCredito;
    return (
      <div
        className={`crm-card crm-card-lineas${(vacio || pendiente) ? " crm-card-disabled" : ""}`}
        {...cardInteractionProps("/Creditoycobranza/DetalleClientesGestionar")}
      >
        <div className="crm-card-top">
          <div className="crm-card-top-left">
            <div className="crm-card-icon crm-icon-banco"><AccountBalanceIcon /></div>
            <h3 className="crm-card-title">Detalle de créditos</h3>
          </div>
          <span className="crm-lineas-badge">Capacidad financiera</span>
        </div>
        {(vacio || pendiente) ? renderSinInfo(pendiente) : (
          <>
            <div className="crm-lineas-tabla scroll">
              {lineasAgrupadas.map((l) => (
                <div key={l.descripcion} className="crm-linea-row">
                  <span className="crm-linea-tipo">{l.descripcion}</span>
                  <span className="crm-linea-monto">{formatMXN(l.total)}</span>
                </div>
              ))}
            </div>
            <div className="crm-lineas-total">
              <span>Total disponible</span>
              <span className="crm-lineas-total-monto">{formatMXN(totalDisponible)}</span>
            </div>
          </>
        )}
        <div className="crm-card-footer">
          <span className="crm-card-last">{(vacio || pendiente) ? "—" : "Ver líneas"}</span>
          <ArrowForwardIosIcon className="crm-card-arrow" />
        </div>
      </div>
    );
  };

  const renderCardReferencias = () => {
    const referencias = datos.referencias;

    const pendiente = SECCIONES_PENDIENTES.referencias;
    return (
      <div
        className={`crm-card crm-card-referencias${(vacio || pendiente) ? " crm-card-disabled" : ""}`}
        // {...cardInteractionProps("/DashClientes/Referencia_Bancaria", pendiente)}
        onClick={() => onDercargarReferenciaBancaria(referencias.idcliente)}

      >
        <div className="crm-card-top">
          <div className="crm-card-top-left">
            <div className="crm-card-icon crm-icon-wallet"><AccountBalanceWalletIcon /></div>
            <h3 className="crm-card-title">Referencias Bancarias</h3>
          </div>
        </div>
        {(vacio || pendiente) ? renderSinInfo(pendiente) : (
          <>
            <div className="crm-ref-count">
              <span className="crm-ref-num">{referencias.total}</span>
              <span className="crm-ref-lbl">Referencias registradas</span>
            </div>
            <div className="crm-ref-chips">
              {referencias.referencia !== "" && (
                <span className="crm-ref-chip">{referencias.referencia}</span>
              )}
            </div>
          </>
        )}
        <div className="crm-card-footer">
          <span className="crm-card-last">{(vacio || pendiente) ? "—" : "Ver referencias"}</span>
          <ArrowForwardIosIcon className="crm-card-arrow" />
        </div>
      </div>
    );
  };

  const renderCardExpedienteDigital = () => {
    // Según el tab activo se lee documentacionMhusa o documentacionJDF
    const seccion = tipoExpedienteDigital === "JDF" ? datos.documentacionJDF : datos.documentacionMhusa;
    const { documentos, vigentes, expirados } = seccion;
    const sinDocumentos = !vacio && documentos === 0 && vigentes === 0 && expirados === 0;
    return (
      <div
        className={`crm-card crm-card-pipeline${vacio ? " crm-card-disabled" : ""}`}
        {...cardInteractionProps("/DashClientes/expedientedigital", false, { linea: tipoExpedienteDigital === "JDF" ? "J" : "O" })}
      >
        <div className="crm-card-top">
          <div className="crm-card-top-left">
            <div className="crm-card-icon crm-icon-credito"><AssignmentIndIcon /></div>
            <h3 className="crm-card-title">Expediente Digital</h3>
          </div>
          {/* Alterna entre expediente Mhusa y JDF; stopPropagation para no disparar la navegación de la card */}
          <div className="crm-exp-digital-tabs" onClick={(e) => e.stopPropagation()}>
            <button
              type="button"
              className={`crm-exp-digital-tab${tipoExpedienteDigital === "Mhusa" ? " activo" : ""}`}
              onClick={() => setTipoExpedienteDigital("Mhusa")}
            >
              MHUSA
            </button>
            <button
              type="button"
              className={`crm-exp-digital-tab${tipoExpedienteDigital === "JDF" ? " activo" : ""}`}
              onClick={() => setTipoExpedienteDigital("JDF")}
            >
              JDF
            </button>
          </div>
        </div>
        {vacio ? renderSinInfo() : sinDocumentos ? (
          <p className="crm-sin-info">
            Este cliente no tiene documentos cargados en {tipoExpedienteDigital === "JDF" ? "JDF" : "Mhusa"}.
          </p>
        ) : (
          <>
            <div className="crm-pipeline-counters">
              <div className="crm-pipeline-counter counter-gris">
                <span className="counter-num">{documentos}</span>
                <span className="counter-lbl">Totales</span>
              </div>
              <div className="crm-pipeline-counter counter-verde">
                <span className="counter-num">{vigentes}</span>
                <span className="counter-lbl">Vigentes</span>
              </div>
              <div className="crm-pipeline-counter counter-rojo">
                <span className="counter-num">{expirados}</span>
                <span className="counter-lbl">Expirados</span>
              </div>
            </div>
            {renderBarraSegmentada(documentos, vigentes, expirados, ["seg-gris", "seg-verde", "seg-rojo"])}
            <div className="crm-barra-leyenda">
              <span className="ley-gris">■ Totales</span>
              <span className="ley-verde">■ Vigentes</span>
              <span className="ley-rojo">■ Expirados</span>
            </div>
          </>
        )}
        <div className="crm-card-footer">
          <span className="crm-card-last">{vacio ? "—" : "Ver expediente digital"}</span>
          <ArrowForwardIosIcon className="crm-card-arrow" />
        </div>
      </div>
    );
  };

  // Total/John Deere/Otras marcas + % John Deere — mismo layout que renderCardVisitas, pero a
  // diferencia de esa card, Maquinaria SÍ permanece inactiva sin cliente seleccionado (siempre
  // es "del cliente", no hay vista agregada de todos los clientes).
  const renderCardMaquinaria = () => {
    const { total, john_deere, otras_marcas, porcentaje_john_deere } = datos.parqueMaquinaria;
    const pendiente = SECCIONES_PENDIENTES.maquinaria;

    return (
      <div
        className={`crm-card crm-card-pipeline crm-card-maquinaria${(vacio || pendiente) ? " crm-card-disabled" : ""}`}
        {...cardInteractionProps("/DashClientes/ParqueMaquinaria", pendiente)}
      >
        <div className="crm-card-top">
          <div className="crm-card-top-left">
            <div className="crm-card-icon crm-icon-maquina"><PrecisionManufacturingIcon /></div>
            <h3 className="crm-card-title">Parque de Maquinaria</h3>
          </div>
        </div>

        {(vacio || pendiente) ? renderSinInfo(pendiente) : (
          <>
            <div className="crm-pipeline-counters crm-visitas-counters">
              <div className="crm-pipeline-counter counter-gris">
                <span className="counter-num">{total}</span>
                <span className="counter-lbl">Maquinaria total</span>
              </div>
              <div className="crm-pipeline-counter counter-verde">
                <span className="counter-num">{john_deere}</span>
                <span className="counter-lbl">John Deere</span>
              </div>
              <div className="crm-pipeline-counter counter-amarillo">
                <span className="counter-num">{otras_marcas}</span>
                <span className="counter-lbl">Otras marcas</span>
              </div>
            </div>

            <div className="crm-exp-progress">
              <div className="crm-exp-progress-header">
                <span>Porcentaje de maquinaria John Deere</span>
                <span className="crm-exp-progress-pct">{Math.round(porcentaje_john_deere)}%</span>
              </div>
              <div className="crm-exp-progress-track">
                <div className="crm-exp-progress-fill" style={{ width: `${Math.round(porcentaje_john_deere)}%` }} />
              </div>
            </div>
          </>
        )}

        <div className="crm-card-footer">
          <span className="crm-card-last">{(vacio || pendiente) ? "—" : "Ver inventario"}</span>
          <ArrowForwardIosIcon className="crm-card-arrow" />
        </div>
      </div>
    );
  };

  // Card de acceso a Cotizaciones de Servicio — no depende de cliente seleccionado, mismo
  // patrón que renderCardReportes (dos accesos internos en vez de una sola navegación).
  const renderCardCotizacionesServicio = () => {
    const puedeVerCatalogo = roles.some((rol) => rol.idrol === "ADCS");
    return (
      <div className="crm-card crm-card-reportes">
        <div className="crm-card-top">
          <div className="crm-card-top-left">
            <div className="crm-card-icon crm-icon-visitas"><RequestQuoteIcon /></div>
            <h3 className="crm-card-title">Cotizaciones de Servicio</h3>
          </div>
        </div>
        <div className="crm-reportes-opciones">
          <div
            className="crm-reportes-opcion"
            role="button"
            tabIndex={0}
            onClick={() => history.push("/DashClientes/CotizacionesServicio")}
            onKeyDown={(e) => e.key === "Enter" && history.push("/DashClientes/CotizacionesServicio")}
          >
            <RequestQuoteIcon />
            <span>Cotizaciones</span>
            <ArrowForwardIosIcon className="crm-reportes-opcion-arrow" />
          </div>
          {puedeVerCatalogo && (
            <div
              className="crm-reportes-opcion"
              role="button"
              tabIndex={0}
              onClick={() => history.push("/DashClientes/CotizacionesServicio/Catalogo")}
              onKeyDown={(e) => e.key === "Enter" && history.push("/DashClientes/CotizacionesServicio/Catalogo")}
            >
              <PlaylistAddIcon />
              <span>Catálogo de servicio</span>
              <ArrowForwardIosIcon className="crm-reportes-opcion-arrow" />
            </div>
          )}
        </div>
      </div>
    );
  };

  // Card de acceso a reportes — no depende de cliente seleccionado
  const renderCardReportes = () => {
    return (
      <div className="crm-card crm-card-reportes">
        <div className="crm-card-top">
          <div className="crm-card-top-left">
            <div className="crm-card-icon crm-icon-visitas"><AssessmentIcon /></div>
            <h3 className="crm-card-title">Reportes</h3>
          </div>
        </div>
        <div className="crm-reportes-opciones">
          <div
            className="crm-reportes-opcion"
            role="button"
            tabIndex={0}
            onClick={() => history.push("/DashClientes/Reportes/ClientesActualizados")}
            onKeyDown={(e) => e.key === "Enter" && history.push("/DashClientes/Reportes/ClientesActualizados")}
          >
            <PersonIcon />
            <span>Información capturada</span>
            <ArrowForwardIosIcon className="crm-reportes-opcion-arrow" />
          </div>
          <div
            className="crm-reportes-opcion"
            role="button"
            tabIndex={0}
            onClick={() => history.push("/DashClientes/Reportes/VisitasProgramadas")}
            onKeyDown={(e) => e.key === "Enter" && history.push("/DashClientes/Reportes/VisitasProgramadas")}
          >
            <EventNoteIcon />
            <span>Visitas</span>
            <ArrowForwardIosIcon className="crm-reportes-opcion-arrow" />
          </div>
          <div
            className="crm-reportes-opcion"
            role="button"
            tabIndex={0}
            onClick={() => history.push("/DashClientes/Reportes/Geolocalizacion")}
            onKeyDown={(e) => e.key === "Enter" && history.push("/DashClientes/Reportes/Geolocalizacion")}
          >
            <LocationOnIcon />
            <span>Geolocalización</span>
            <ArrowForwardIosIcon className="crm-reportes-opcion-arrow" />
          </div>
          <div
            className="crm-reportes-opcion"
            role="button"
            tabIndex={0}
            onClick={() => history.push("/DashClientes/Reportes/CoberturaVisitas")}
            onKeyDown={(e) => e.key === "Enter" && history.push("/DashClientes/Reportes/CoberturaVisitas")}
          >
            <EventNoteIcon />
            <span>Cobertura de visitas</span>
            <ArrowForwardIosIcon className="crm-reportes-opcion-arrow" />
          </div>
          <div
            className="crm-reportes-opcion"
            role="button"
            tabIndex={0}
            onClick={() => history.push("/DashClientes/Reportes/CoberturaCartera")}
            onKeyDown={(e) => e.key === "Enter" && history.push("/DashClientes/Reportes/CoberturaCartera")}
          >
            <AssessmentIcon />
            <span>Cobertura de cartera</span>
            <ArrowForwardIosIcon className="crm-reportes-opcion-arrow" />
          </div>
        </div>
      </div>
    );
  };

  // Card de acceso a configuraciones — no depende de cliente seleccionado
  const renderCardConfiguraciones = () => {

  //ADMK REWR Roles para reportes de configuracion

  if(roles.some(rol => rolesPermitidos.includes(rol.idrol)) )
    return (
      <div className="crm-card crm-card-reportes">
        <div className="crm-card-top">
          <div className="crm-card-top-left">
            <div className="crm-card-icon crm-icon-visitas"><AssessmentIcon /></div>
            <h3 className="crm-card-title">Indicadores</h3>
          </div>
        </div>
        <div className="crm-reportes-opciones">
          <div
            className="crm-reportes-opcion"
            role="button"
            tabIndex={0}
            onClick={() => history.push("/DashClientes/ConfiguracionObjetivos")}
            onKeyDown={(e) => e.key === "Enter" && history.push("DashClientes/ConfiguracionObjetivos")}
          >
            <PersonIcon />
            <span>Captura de Objetivos</span>
            <ArrowForwardIosIcon className="crm-reportes-opcion-arrow" />
          </div>
        </div>

        <div className="crm-reportes-opciones">
          <div
            className="crm-reportes-opcion"
            role="button"
            tabIndex={0}
            onClick={() => history.push("/DashClientes/IndicadoresVisitas")}
            onKeyDown={(e) => e.key === "Enter" && history.push("/DashClientes/IndicadoresVisitas")}
          >
            <EventNoteIcon />
            <span>Indicadores de Visitas</span>
            <ArrowForwardIosIcon className="crm-reportes-opcion-arrow" />
          </div>
        </div>

        <div className="crm-reportes-opciones">
          <div
            className="crm-reportes-opcion"
            role="button"
            tabIndex={0}
            onClick={() => history.push("/DashClientes/IndicadoresCotizaciones")}
            onKeyDown={(e) => e.key === "Enter" && history.push("/DashClientes/IndicadoresCotizaciones")}
          >
            <DescriptionIcon />
            <span>Indicadores de cotizaciones</span>
            <ArrowForwardIosIcon className="crm-reportes-opcion-arrow" />
          </div>
        </div>
      </div>
    );
    else 
      return <></>
  };

  const renderDropdown = () => {
    if (!mostrarDropdown) return null;
    return (
      <div className="crm-search-dropdown">
        {resultados.length > 0 ? (
          resultados.map((c) => (
            <div key={c.idcliente} className="crm-search-result" onClick={() => handleSelectCliente(c)}>
              <div className="crm-res-avatar"><AccountCircleOutlinedIcon /></div>
              <div className="crm-res-info">
                <span className="crm-res-nombre">{c.razon_social}</span>
                <span className="crm-res-meta">
                  <BadgeIcon /> {c.rfc}
                </span>
              </div>
              <ArrowForwardIosIcon className="crm-res-arrow" />
            </div>
          ))
        ) : (
          <div className="crm-search-no-result">
            <span>No se encontraron clientes con "{busqueda}"</span>
            {roles.some(rol => rolesPermitidos.includes(rol.idrol)) && (
              <button className="crm-btn-crear-dropdown" onClick={() => navegarA("/DashClientes/Detalle")}>
                <AddIcon /> Crear nuevo cliente
              </button>
            )}
          </div>
        )}
      </div>
    );
  };

  // ── JSX PRINCIPAL ───────────────────────────────────────────────
  return (
    <div className="crm-main-screen">
      <LoaderScreen loader={loader} />
      <div className="crm-body">
        <div className="crm-page">
          <div className="crm-module">
            <div className="crm-module-head">
              <div className="crm-head-grid">
                <div className="crm-head-left" />
                <div className="crm-head-center">
                  <h1 className="crm-module-title">GESTIÓN DE CLIENTES</h1>
                </div>
                <div className="crm-head-right" />
              </div>
              <div className="crm-head-divider">
                <div className="crm-head-divider-dot" />
              </div>
            </div>
            <div className="crm-home-wrapper scroll">

              {/* Buscador principal */}
              <div className="crm-home-search-section" ref={searchRef}>
                <div className={`crm-home-search-bar${mostrarDropdown ? " search-open" : ""}`}>
                  <button type="button" className="crm-search-icon-btn" title="Búsqueda avanzada" onClick={() => setModalBusquedaAvanzada(true)}>
                    <SearchIcon className="crm-search-icon" />
                  </button>
                  <input
                    type="text"
                    className="crm-search-input"
                    placeholder="Buscar por razón social o RFC..."
                    value={busqueda}
                    onChange={handleBusquedaChange}
                    onFocus={() => setMostrarDropdown(true)}
                    autoComplete="off"
                  />
                  {busqueda && (
                    <button className="crm-search-clear" onClick={handleLimpiar}>
                      <ClearIcon />
                    </button>
                  )}
                  <div className="crm-search-divider" />
                  <button type="button" className="crm-search-filter-btn" onClick={() => setModalBusquedaAvanzada(true)}>
                    <TuneIcon /> Filtros
                  </button>
                </div>
                {renderDropdown()}
              </div>

              {/* Grid de tarjetas — siempre visible */}
              <div className="crm-home-grid scroll">
                {renderCardExpediente()}
                {renderCardSolicitudes()}
                {renderCardCotizaciones()}
                {renderCardVisitas()}
                {renderCardLineas()}
                {renderCardReferencias()}
                {renderCardExpedienteDigital()}
                {renderCardMaquinaria()}
                {renderCardCotizacionesServicio()}
                <div className="crm-grid-break" />
                {renderCardReportes()}
                {renderCardConfiguraciones()}
              </div>
            </div>
          </div>
        </div>
      </div>

      <CRM_ModalBusquedaAvanzada
        open={modalBusquedaAvanzada}
        onClose={() => setModalBusquedaAvanzada(false)}
        clientes={clientes}
        onSeleccionarCliente={handleSelectCliente}
      />
    </div>
  );
};
